/**
 * Styles for the Ability Grounds Cast Bench.
 */
import React from 'react';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../../theme';

export const benchStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
  },
  targetRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: SPACE.xs,
  },
  targetPill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    padding: `${SPACE.xs}px 4px`,
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    color: COLORS.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  targetPillActive: {
    background: 'linear-gradient(135deg, rgba(230, 160, 40, 0.22), rgba(180, 120, 20, 0.12))',
    border: `1px solid ${PLAYER_UI.brassBright}`,
    boxShadow: '0 0 10px rgba(196, 164, 105, 0.35)',
    color: COLORS.textPrimary,
  },
  targetPillLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.bold,
  },
  targetPillDist: {
    fontSize: 9,
    color: COLORS.textMuted,
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
    background: 'rgba(0, 0, 0, 0.35)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    padding: `4px ${SPACE.sm}px`,
    marginBottom: SPACE.xs,
  },
  searchIcon: {
    fontSize: FONT_SIZE.sm,
    opacity: 0.6,
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: COLORS.textPrimary,
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.sm,
  },
  clearSearchBtn: {
    background: 'transparent',
    border: 'none',
    color: COLORS.textMuted,
    cursor: 'pointer',
    fontSize: FONT_SIZE.xs,
    padding: '2px 4px',
  },
  abilityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    maxHeight: 120,
    overflowY: 'auto',
    background: 'rgba(10, 14, 20, 0.6)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    padding: 4,
  },
  abilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: '4px 6px',
    borderRadius: RADII.sm,
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    border: '1px solid transparent',
  },
  abilityRowSelected: {
    background: 'rgba(196, 164, 105, 0.14)',
    border: '1px solid rgba(196, 164, 105, 0.45)',
    borderLeft: `3px solid ${PLAYER_UI.brassBright}`,
    boxShadow: 'inset 0 0 8px rgba(196, 164, 105, 0.1)',
  },
  iconWrapper: {
    flexShrink: 0,
  },
  abilityMetaCol: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  abilityNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
  },
  abilityName: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textPrimary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  abilityId: {
    fontSize: 9,
    color: COLORS.textMuted,
    fontFamily: FONTS.mono,
  },
  abilityCostCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
    flexShrink: 0,
  },
  manaChip: {
    fontSize: 9,
    fontFamily: FONTS.mono,
    color: COLORS.mana,
    fontWeight: FONT_WEIGHT.bold,
  },
  freeChip: {
    fontSize: 9,
    fontFamily: FONTS.mono,
    color: COLORS.textMuted,
  },
  cdChip: {
    fontSize: 8,
    fontFamily: FONTS.mono,
    color: COLORS.textSecondary,
  },
  emptyList: {
    padding: SPACE.md,
    textAlign: 'center',
    color: COLORS.textMuted,
    fontSize: FONT_SIZE.sm,
  },
  factCard: {
    marginTop: SPACE.xs,
    padding: SPACE.sm,
    background: 'rgba(16, 22, 30, 0.85)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
  },
  factHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  factTitleCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  factKicker: {
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: PLAYER_UI.brassBright,
  },
  factTitle: {
    fontSize: FONT_SIZE.md,
    color: COLORS.textPrimary,
  },
  factDesc: {
    margin: 0,
    fontSize: FONT_SIZE.xs,
    color: COLORS.textSecondary,
    lineHeight: 1.35,
  },
  factsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: SPACE.xs,
    paddingTop: SPACE.xxs,
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  },
  factItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  factLabel: {
    fontSize: 9,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
  },
  factVal: {
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.semibold,
    color: PLAYER_UI.brassBright,
    fontFamily: FONTS.mono,
  },
  hotbarSection: {
    marginTop: SPACE.xs,
    padding: SPACE.xs,
    background: 'rgba(0, 0, 0, 0.25)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
  },
  hotbarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  hotbarTitle: {
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.semibold,
    color: COLORS.textPrimary,
  },
  hotbarSub: {
    fontSize: 9,
    color: COLORS.textMuted,
  },
  slotPillsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: 3,
  },
  slotPill: {
    padding: '4px 0',
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    color: COLORS.textSecondary,
    fontSize: FONT_SIZE.xs,
    fontFamily: FONTS.mono,
    fontWeight: FONT_WEIGHT.bold,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.12s ease',
  },
  slotPillActive: {
    background: 'rgba(196, 164, 105, 0.25)',
    border: `1px solid ${PLAYER_UI.brassBright}`,
    boxShadow: '0 0 6px rgba(196, 164, 105, 0.4)',
    color: PLAYER_UI.brassBright,
  },
  statusBox: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
    padding: `${SPACE.xxs}px ${SPACE.xs}px`,
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: RADII.sm,
    border: '1px solid rgba(255, 255, 255, 0.04)',
    marginTop: SPACE.xs,
  },
  statusDot: {
    color: COLORS.success,
    fontSize: 9,
  },
  statusText: {
    fontSize: FONT_SIZE.xs,
    color: COLORS.textSecondary,
    fontFamily: FONTS.mono,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
